import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router-dom";
import { RootState, useAppDispatch } from "@store/index";
import {
  ShippingCaseResponse,
  MappedOffer,
  CargoDocs,
} from "@pages/shippingView/types";
import { Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";
import { EditOutlined, InsertDriveFileOutlined } from "@mui/icons-material";
import { mapOfferDetailsData } from "@pages/shippingView/helpers/map-offer-details";
import Section from "@pages/offerView/components/section";
import AddedShippingService from "@atoms/addedShippingService";
import { DataGrid } from "@mui/x-data-grid";
import { Columns } from "@pages/shippingView/components/columns";
import { cleanShippingCase } from "@pages/shippingCreate/createShippingSlice";
import CompleteDialog from "./components/completeDialog";
import { hideWarning } from "@pages/shippingView/shippingSlice";
import Warning from "@atoms/warning";
import {
  KEY_INFORMATION_EDIT_ROLES,
  KEY_INFORMATION_FULL_ACCESS_ROLES,
} from "@pages/shippingView/helpers/tabs-mapping";

const Information: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [activityComplete, setActivityCompleted] = useState<number | null>(
    null,
  );
  const { shipping } = useOutletContext<{ shipping: ShippingCaseResponse }>();
  const role = useSelector((state: RootState) => state.auth.user?.role) || "";
  const error = useSelector(
    (state: RootState) => state.viewShipping.shipping.shipping.error,
  );
  const success = useSelector(
    (state: RootState) => state.viewShipping.shipping.shipping.success,
  );
  const mappedOffer: MappedOffer = mapOfferDetailsData(shipping);
  const Rows =
    shipping.cargo_docs?.map((document) => {
      const key = (document.name?.toUpperCase() ||
        "null") as keyof typeof CargoDocs;

      return {
        id: document.name,
        docName: CargoDocs[key] || document.name,
        file: {
          name: document.path,
          src: document.presigned_url,
        },
        version: document.original ? "Original" : "Copy",
        user: document.updated_by,
      };
    }) || [];

  const handleEditClick = () => {
    dispatch(cleanShippingCase());
    navigate(
      `/offers/${shipping.offer.id}/shipping-case/${shipping.id}/update`,
    );
  };
  const closeCallback = () => dispatch(hideWarning("shipping"));

  const canEditKeyInformation = (
    KEY_INFORMATION_EDIT_ROLES as readonly string[]
  ).includes(role);
  const hasFullAccess = (
    KEY_INFORMATION_FULL_ACCESS_ROLES as readonly string[]
  ).includes(role);

  return (
    <>
      {!!error && (
        <Warning text={error} severity="error" closeCallback={closeCallback} />
      )}
      {!!success && (
        <Warning
          text={success}
          severity="success"
          closeCallback={closeCallback}
        />
      )}
      <Stack gap={1}>
        {(canEditKeyInformation || hasFullAccess) && (
          <Box gap={2} sx={{ display: "flex", mb: 2 }}>
            {canEditKeyInformation && (
              <Button
                size="small"
                startIcon={<EditOutlined />}
                sx={{ color: "rgba(0, 0, 0, 0.87)" }}
                onClick={handleEditClick}
              >
                Edit Key Information
              </Button>
            )}
            {hasFullAccess && (
              <Button
                size="small"
                startIcon={<InsertDriveFileOutlined />}
                sx={{ color: "rgba(0, 0, 0, 0.87)" }}
                onClick={() => navigate(`/offers/${shipping.offer.id}`)}
              >
                Go to offer
              </Button>
            )}
          </Box>
        )}
        {Object.entries(mappedOffer).map(([section, data]: [any, any]) => (
          <Section key={section} title={section} data={data} />
        ))}
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography sx={{ mb: 1 }}>Cargo Summary</Typography>
          <Paper sx={{ p: 0, mt: 2 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                p: "12px 16px",
                borderBottom: "1px solid rgba(0,0,0,0.12)",
              }}
            >
              <Typography fontSize="14px" fontWeight="500">
                Total Pieces
              </Typography>
              <Typography fontSize="14px" fontWeight="500">
                Total Net Weight (ton)
              </Typography>
              <Typography fontSize="14px" fontWeight="500">
                Total Gross Weight (ton)
              </Typography>
              <Typography fontSize="14px" fontWeight="500">
                Number of Containers
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                p: "12px 16px",
              }}
            >
              <Typography fontSize="14px">
                {shipping.total_pieces ?? ""}
              </Typography>
              <Typography fontSize="14px">
                {shipping.total_net_weight ?? ""}
              </Typography>
              <Typography fontSize="14px">
                {shipping.total_gross_weight ?? ""}
              </Typography>
              <Typography fontSize="14px">
                {shipping.total_containers ?? ""}
              </Typography>
            </Box>
          </Paper>
        </Paper>

        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography sx={{ mb: 1 }}>What do we have to do?</Typography>
          <Box gap={2} display="flex" flexDirection="row">
            <Stack width="100%" gap={2}>
              <Chip
                sx={{
                  width: "100%",
                  backgroundColor: "rgba(239, 108, 0, 0.04)",
                }}
                label="To do"
                color="warning"
                size="small"
                variant="outlined"
              />
              {shipping.activities &&
                (
                  shipping.activities.filter((service) => !service.completed) ||
                  []
                ).map((service) => (
                  <AddedShippingService
                    key={service.id}
                    props={{
                      route: service.route,
                      title: service.description as string,
                      createdAt: service.created_at,
                      completeCallback: canEditKeyInformation
                        ? () => setActivityCompleted(service.id as number)
                        : undefined,
                    }}
                  />
                ))}
            </Stack>
            <Stack width="100%" gap={2}>
              <Chip
                sx={{
                  width: "100%",
                  backgroundColor: "rgba(46, 125, 50, 0.04)",
                }}
                label="Completed"
                color="success"
                size="small"
                variant="outlined"
              />
              {shipping.activities &&
                (
                  shipping.activities.filter((service) => service.completed) ||
                  []
                ).map((service) => (
                  <AddedShippingService
                    key={service.id}
                    props={{
                      route: service.route,
                      title: service.description as string,
                      createdAt:
                        service.completion_date ??
                        service.execution_date ??
                        service.created_at,
                    }}
                  />
                ))}
            </Stack>
          </Box>
        </Paper>
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography sx={{ mb: 1 }}>List Of Cargo Docs</Typography>
          <DataGrid
            sx={{ mt: 1 }}
            columns={Columns}
            rows={Rows}
            disableRowSelectionOnClick
            hideFooter
          />
        </Paper>
        {activityComplete && (
          <CompleteDialog
            sid={shipping.id}
            activityId={activityComplete}
            handleClose={() => setActivityCompleted(null)}
          />
        )}
      </Stack>
    </>
  );
};

export default Information;
